import ModalPortal from '@/components/ModalPortal';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Clock3, LogOut, RefreshCw, ShieldCheck, TriangleAlert } from 'lucide-react';
import { useAuth } from '@/auth/AuthContext';
import { SESSION_EXPIRED_EVENT } from '@/api/axios';

const WARNING_THRESHOLD_SECONDS = 5 * 60;

const formatRemainingTime = (seconds: number) => {
    const safeSeconds = Math.max(0, seconds);
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = safeSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
};

const SessionTimeoutHandler = () => {
    const { user, sessionExpiresAt, renewSession, logout } = useAuth();
    const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
    const [renewing, setRenewing] = useState(false);
    const [renewError, setRenewError] = useState('');
    const expiredAtRef = useRef<number | null>(null);

    useEffect(() => {
        if (!user || !sessionExpiresAt) {
            setRemainingSeconds(null);
            expiredAtRef.current = null;
            return;
        }

        expiredAtRef.current = null;

        const updateRemainingTime = () => {
            const remaining = Math.max(0, Math.ceil((sessionExpiresAt - Date.now()) / 1000));
            setRemainingSeconds(remaining);

            if (remaining === 0 && expiredAtRef.current !== sessionExpiresAt) {
                expiredAtRef.current = sessionExpiresAt;
                window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, {
                    detail: { expiresAt: sessionExpiresAt },
                }));
            }
        };

        updateRemainingTime();
        const interval = window.setInterval(updateRemainingTime, 1000);
        document.addEventListener('visibilitychange', updateRemainingTime);

        return () => {
            window.clearInterval(interval);
            document.removeEventListener('visibilitychange', updateRemainingTime);
        };
    }, [sessionExpiresAt, user]);

    useEffect(() => {
        setRenewError('');
        setRenewing(false);
    }, [sessionExpiresAt]);

    const progress = useMemo(() => {
        if (remainingSeconds === null) return 0;
        return Math.min(100, Math.max(0, (remainingSeconds / WARNING_THRESHOLD_SECONDS) * 100));
    }, [remainingSeconds]);

    const shouldWarn = Boolean(
        user
        && sessionExpiresAt
        && remainingSeconds !== null
        && remainingSeconds > 0
        && remainingSeconds <= WARNING_THRESHOLD_SECONDS
    );

    const handleRenew = async () => {
        setRenewing(true);
        setRenewError('');
        try {
            await renewSession();
        } catch {
            setRenewError('Não foi possível renovar agora. Verifique sua conexão e tente novamente.');
            setRenewing(false);
        }
    };

    return (
        <AnimatePresence>
            {shouldWarn && remainingSeconds !== null && (
                <ModalPortal><motion.div
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-[#05070c]/80 p-4 backdrop-blur-md"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    role="presentation"
                >
                    <motion.section
                        role="alertdialog"
                        aria-modal="true"
                        aria-labelledby="session-warning-title"
                        aria-describedby="session-warning-description"
                        initial={{ opacity: 0, y: 24, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 16, scale: 0.98 }}
                        transition={{ type: 'spring', stiffness: 260, damping: 24 }}
                        className="relative w-full max-w-md overflow-hidden rounded-[2rem] border border-amber-400/20 bg-[#111827] p-6 shadow-2xl shadow-black/60 sm:p-8"
                    >
                        <div className="pointer-events-none absolute -right-20 -top-24 h-52 w-52 rounded-full bg-amber-400/10 blur-3xl" />

                        <div className="relative">
                            <div className="mb-6 flex items-start gap-4">
                                <motion.div
                                    animate={{ scale: [1, 1.08, 1] }}
                                    transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                                    className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-amber-400/20 bg-amber-400/10 text-amber-300"
                                >
                                    <Clock3 size={28} />
                                </motion.div>
                                <div>
                                    <p className="mb-1 text-[9px] font-black uppercase tracking-[0.25em] text-amber-400">
                                        Segurança da sessão
                                    </p>
                                    <h2 id="session-warning-title" className="text-2xl font-black tracking-tight text-white">
                                        Sua sessão está expirando
                                    </h2>
                                    <p id="session-warning-description" className="mt-2 text-sm leading-relaxed text-slate-400">
                                        Renove agora para continuar trabalhando por mais 15 minutos sem precisar entrar novamente.
                                    </p>
                                </div>
                            </div>

                            <div className="mb-6 rounded-2xl border border-white/5 bg-[#0b0f1a] p-5 text-center">
                                <p className="mb-2 text-[9px] font-black uppercase tracking-[0.22em] text-slate-500">
                                    Tempo restante
                                </p>
                                <motion.p
                                    key={remainingSeconds}
                                    initial={{ opacity: 0.6, scale: 0.98 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="font-mono text-4xl font-black tracking-wider text-amber-300"
                                    aria-live="polite"
                                >
                                    {formatRemainingTime(remainingSeconds)}
                                </motion.p>
                                <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-white/5">
                                    <motion.div
                                        className="h-full rounded-full bg-gradient-to-r from-red-500 via-amber-500 to-amber-300"
                                        animate={{ width: `${progress}%` }}
                                        transition={{ duration: 0.4, ease: 'linear' }}
                                    />
                                </div>
                            </div>

                            {renewError && (
                                <motion.div
                                    initial={{ opacity: 0, y: -5 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="mb-5 flex gap-3 rounded-2xl border border-red-500/20 bg-red-500/10 p-4 text-xs font-semibold text-red-300"
                                >
                                    <TriangleAlert size={18} className="shrink-0" />
                                    <span>{renewError}</span>
                                </motion.div>
                            )}

                            <button
                                type="button"
                                autoFocus
                                disabled={renewing}
                                onClick={handleRenew}
                                className="ui-button-primary w-full py-4"
                            >
                                {renewing ? (
                                    <>
                                        <RefreshCw size={17} className="animate-spin" />
                                        Renovando sessão...
                                    </>
                                ) : (
                                    <>
                                        <ShieldCheck size={18} />
                                        Continuar conectado
                                    </>
                                )}
                            </button>

                            <button
                                type="button"
                                disabled={renewing}
                                onClick={() => void logout()}
                                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500 transition-colors hover:bg-white/5 hover:text-slate-300 disabled:opacity-40"
                            >
                                <LogOut size={15} />
                                Sair agora
                            </button>
                        </div>
                    </motion.section>
                </motion.div></ModalPortal>
            )}
        </AnimatePresence>
    );
};

export default SessionTimeoutHandler;
